import { useEffect, useRef } from 'react';
// mantine
import { Pill, UnstyledButton } from '@mantine/core';
// icons
import { Pause, Play } from 'lucide-react';
// utils
import { cn } from '@/utils';
import { TVoiceOption } from '@/pages/Settings/components/Voice/Voice.types';
// styles
import styles from '@/pages/Settings/components/Voice/components/VoiceSelect/components/VoiceOption/VoiceOption.module.css';

interface VoiceOptionProps {
    voice: TVoiceOption;
    isActive: boolean;
    onPlayToggle: (voiceId?: string) => void;
}

const VoiceOption = ({ voice, isActive, onPlayToggle }: VoiceOptionProps) => {
    const audioRef = useRef<HTMLAudioElement | null>(null);

    useEffect(() => {
        return () => {
            if (audioRef.current) {
                audioRef.current.pause();
                /* eslint-disable-next-line unicorn/no-null */
                audioRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        if (isActive) {
            audioRef.current = new Audio(voice.preview_url);
            audioRef.current.play();
            audioRef.current.addEventListener('ended', () => {
                onPlayToggle();
            });
        } else {
            audioRef.current?.pause();
        }
        return () => {
            if (audioRef.current) {
                audioRef.current.pause();
                /* eslint-disable-next-line unicorn/no-null */
                audioRef.current = null;
            }
        };
    }, [isActive]);

    const handleToggle = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        event.preventDefault();

        onPlayToggle(voice.voice_id);
    };

    const iconClassName =
        'text-2xl relative z-1 text-black/50 drop-shadow-sm/50';

    return (
        <div
            className={cn(
                'flex gap-4',
                styles.sample,
                isActive && styles.isPlaying
            )}
        >
            <UnstyledButton
                className="flex relative overflow-hidden items-center justify-center w-16 h-16 rounded-lg shadow-xs shrink-0"
                onClick={handleToggle}
            >
                <div
                    className={styles.bg}
                    style={{
                        backgroundImage: `conic-gradient(from 45deg, #ffd1dc, #e0f7fa, #d1c4e9, #f0f4c3, #ffccbc, #b2dfdb, #ffd1dc)`
                    }}
                />
                {isActive ? (
                    <Pause className={iconClassName} />
                ) : (
                    <Play className={iconClassName} />
                )}
            </UnstyledButton>
            <div>
                <div className="text-sm font-semibold">{voice.name}</div>
                <div className="text-xs text-gray-6 mb-1">
                    {voice.description}
                </div>
                <div className="flex flex-wrap gap-1">
                    {voice.labels.map((label) => (
                        <Pill
                            className="capitalize"
                            color="gray.4"
                            key={label}
                            size="xs"
                        >
                            {label}
                        </Pill>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default VoiceOption;
